import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  TrendingUp,
  MoreHorizontal,
  MapPin,
  FileCheck2,
  PenLine,
  Camera,
  Clock,
  ChevronRight,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { KpiCard } from "@/components/KpiCard";

/* ---------------------------------------------------------------------------
   Datos de ejemplo del dominio (tasaciones). Se reemplazan por la API + TanStack
   Query cuando el backend exponga los endpoints. Hoy son estáticos como guía.
--------------------------------------------------------------------------- */

const kpis = [
  {
    label: "Cotizaciones pendientes",
    value: "12",
    delta: "+17%",
    tint: "bg-primary/10",
    color: "#4cb648",
    spark: "M0,40 C30,38 45,26 75,30 C105,34 120,14 150,16 C175,18 188,10 200,8",
  },
  {
    label: "Expedientes activos",
    value: "8",
    delta: "+5%",
    tint: "bg-chart-2/10",
    color: "#6d8fd6",
    spark: "M0,30 C28,32 44,20 72,24 C100,26 120,14 150,16 C175,18 188,22 200,14",
  },
  {
    label: "Completados (mes)",
    value: "24",
    delta: "+44%",
    tint: "bg-chart-4/10",
    color: "#e9a23b",
    spark: "M0,42 L40,42 L40,30 L95,30 L95,20 L150,20 L150,10 L200,10",
  },
];

const flujo = [
  { dia: "Lun", ingresadas: 3, completadas: 2 },
  { dia: "Mar", ingresadas: 5, completadas: 3 },
  { dia: "Mié", ingresadas: 4, completadas: 4 },
  { dia: "Jue", ingresadas: 6, completadas: 5 },
  { dia: "Vie", ingresadas: 5, completadas: 6 },
  { dia: "Sáb", ingresadas: 2, completadas: 3 },
  { dia: "Dom", ingresadas: 1, completadas: 2 },
];

const actividad = [
  { exp: "EXP-2024-002", detalle: "Viñedos del Sur · pasó a Revisión", fecha: "Hoy 09:20", icon: FileCheck2, tint: "bg-primary/10", color: "text-primary" },
  { exp: "EXP-2024-001", detalle: "Viñedos del Valle · registro de terreno", fecha: "Hoy 08:05", icon: Camera, tint: "bg-chart-2/10", color: "text-chart-2" },
  { exp: "EXP-2024-004", detalle: "Banco Central · esperando firma 2", fecha: "Ayer 17:40", icon: PenLine, tint: "bg-chart-4/10", color: "text-chart-4" },
  { exp: "EXP-2024-003", detalle: "Hacienda Santa María · docs pendientes", fecha: "Ayer 15:12", icon: Clock, tint: "bg-chart-3/10", color: "text-chart-3" },
  { exp: "EXP-2024-001", detalle: "Viñedos del Valle · derechos de agua cargados", fecha: "Ayer 11:30", icon: MapPin, tint: "bg-primary/10", color: "text-primary" },
];

const vencimientos = [
  { exp: "EXP-2024-004", cliente: "Banco Central", dias: 2, avance: 90 },
  { exp: "EXP-2024-003", cliente: "Hacienda Santa María", dias: 5, avance: 30 },
];

const equipo = [
  { ini: "MG", from: "#cfe6d3", to: "#93cf9c", fg: "#2f7a2e" },
  { ini: "LT", from: "#f6d9c8", to: "#e6a985", fg: "#a05a34" },
  { ini: "CR", from: "#cdd9f2", to: "#8fa6da", fg: "#3f5aa0" },
  { ini: "MV", from: "#e6d5f0", to: "#c4a5db", fg: "#6b3f8f" },
];

const porTipo = [
  { tipo: "Agrícola", n: 5 },
  { tipo: "Forestal", n: 2 },
  { tipo: "Agroindustrial", n: 1 },
];

export function Dashboard() {
  return (
    <div className="px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div className="flex flex-col gap-8 xl:flex-row">
        {/* -------- Columna principal -------- */}
        <div className="flex min-w-0 flex-1 flex-col gap-7">
          {/* Hero */}
          <section className="rounded-3xl bg-[#17181b] p-6 text-white sm:p-7 dark:ring-1 dark:ring-white/10">
            <div className="flex items-start justify-between">
              <span className="text-base font-bold">Valor tasado · Enero</span>
              <span className="flex items-center gap-1 text-sm font-semibold text-[#4cd06a]">
                <TrendingUp className="h-4 w-4" />
                +12%
              </span>
            </div>
            <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
              <div className="text-4xl font-extrabold tracking-tight">$4.850 M</div>
            </div>
          </section>

          {/* KPIs */}
          <section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-extrabold tracking-tight">Resumen operativo</h2>
              <div className="flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm font-semibold text-muted-foreground">
                Mes
                <ChevronRight className="h-3.5 w-3.5 rotate-90" />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {kpis.map((k) => (
                <KpiCard
                  key={k.label}
                  label={k.label}
                  value={k.value}
                  delta={k.delta}
                  tint={k.tint}
                  color={k.color}
                  spark={k.spark}
                />
              ))}
            </div>
          </section>

          {/* Flujo de tasaciones */}
          <section className="rounded-3xl border bg-card p-6">
            <div className="mb-4 flex flex-wrap items-center gap-4">
              <h2 className="text-xl font-extrabold tracking-tight">Flujo de tasaciones</h2>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                  <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                  Completadas
                </span>
                <span className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                  <span className="h-2.5 w-2.5 rounded-full bg-chart-3" />
                  Ingresadas
                </span>
              </div>
              <div className="ml-auto flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm font-semibold text-muted-foreground">
                Semanal
                <ChevronRight className="h-3.5 w-3.5 rotate-90" />
              </div>
            </div>
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={flujo} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gComp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4cb648" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#4cb648" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="rgba(125,129,136,0.18)" />
                  <XAxis dataKey="dia" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#a6aab0" }} />
                  <YAxis tickLine={false} axisLine={false} width={40} tick={{ fontSize: 12, fill: "#a6aab0" }} />
                  <Tooltip
                    cursor={{ stroke: "rgba(125,129,136,0.35)", strokeWidth: 1 }}
                    contentStyle={{
                      background: "var(--popover)",
                      border: "1px solid var(--border)",
                      borderRadius: 12,
                      color: "var(--popover-foreground)",
                      fontSize: 13,
                    }}
                  />
                  <Area type="monotone" dataKey="completadas" stroke="#4cb648" strokeWidth={2.6} fill="url(#gComp)" isAnimationActive={false} />
                  <Line type="monotone" dataKey="ingresadas" stroke="#e08a5a" strokeWidth={2.6} strokeDasharray="4 6" dot={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Equipo + tipos */}
          <div className="grid gap-5 md:grid-cols-2">
            <div className="rounded-3xl border bg-card p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-extrabold">Equipo en terreno</h3>
                <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="mt-5 flex items-center gap-3">
                {equipo.map((m) => (
                  <div
                    key={m.ini}
                    className="flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold"
                    style={{ background: `linear-gradient(135deg, ${m.from}, ${m.to})`, color: m.fg }}
                  >
                    {m.ini}
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border bg-card p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-extrabold">Por tipo de propiedad</h3>
                <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3">
                {porTipo.map((t) => (
                  <div key={t.tipo} className="rounded-2xl bg-muted p-4">
                    <div className="text-xs font-semibold text-muted-foreground">{t.tipo}</div>
                    <div className="mt-1.5 text-xl font-extrabold">{t.n}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* -------- Rail derecho -------- */}
        <aside className="flex w-full flex-col gap-6 xl:w-[21.875rem] xl:flex-none xl:border-l xl:border-border xl:pl-8">
          {/* Actividad reciente */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-xl font-extrabold tracking-tight">Actividad reciente</h2>
              <div className="flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm font-semibold text-muted-foreground">
                Enero 2026
                <ChevronRight className="h-3.5 w-3.5 rotate-90" />
              </div>
            </div>
            <div className="flex flex-col">
              {actividad.map((a, i) => (
                <div key={i} className="flex items-center gap-3 py-3">
                  <div className={`flex h-11 w-11 flex-none items-center justify-center rounded-[0.8125rem] ${a.tint} ${a.color}`}>
                    <a.icon className="h-[1.125rem] w-[1.125rem]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold">{a.exp}</div>
                    <div className="truncate text-xs text-muted-foreground">{a.detalle}</div>
                  </div>
                  <div className="flex-none text-xs font-medium text-muted-foreground">{a.fecha}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Próximos vencimientos */}
          <div className="mt-auto rounded-3xl border p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-extrabold tracking-tight">Próximos vencimientos</h2>
            </div>
            <div className="flex flex-col gap-3">
              {vencimientos.map((v) => (
                <div key={v.exp} className="rounded-2xl bg-primary/10 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-extrabold">{v.exp}</span>
                    <Badge variant={v.dias <= 3 ? "destructive" : "secondary"}>
                      {v.dias} {v.dias === 1 ? "día" : "días"}
                    </Badge>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{v.cliente}</div>
                  <div className="mt-3 flex items-center gap-2">
                    <Progress value={v.avance} className="h-1.5" />
                    <span className="text-xs font-bold text-muted-foreground">{v.avance}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
